import { useState, useEffect, useMemo } from 'react';
import { FileSpreadsheet, FileText, Users, TrendingUp, Clock, BookOpen } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/molecules/Card';
import { Button } from '@/components/atoms/Button';
import { Select } from '@/components/atoms/Select';
import { Input } from '@/components/atoms/Input';
import { Badge } from '@/components/atoms/Badge';
import { downloadReport, getCompanyLearningStats, type CompanyLearningStats } from '@/services/api/company.api';
import { useEmployees } from '@/hooks/useCompanyData';
import { formatTimeSpent } from '@/utils/formatTime';
import styles from './ReportsPage.module.scss';

type ReportPeriod = 'current_month' | 'last_month' | 'custom';

export function ReportsPage() {
  const [reportPeriod, setReportPeriod] = useState<ReportPeriod>('current_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [learningStats, setLearningStats] = useState<CompanyLearningStats | null>(null);
  const { employees, isLoading } = useEmployees();

  useEffect(() => {
    getCompanyLearningStats()
      .then(setLearningStats)
      .catch(() => setLearningStats(null));
  }, []);

  useEffect(() => {
    const now = new Date();
    if (reportPeriod === 'current_month') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setStartDate(start.toISOString().split('T')[0] ?? '');
      setEndDate(end.toISOString().split('T')[0] ?? '');
    } else if (reportPeriod === 'last_month') {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      setStartDate(start.toISOString().split('T')[0] ?? '');
      setEndDate(end.toISOString().split('T')[0] ?? '');
    }
  }, [reportPeriod]);

  const handleDownloadExcel = async () => {
    try {
      setIsDownloadingExcel(true);
      await downloadReport('xlsx', 'employees');
    } catch (error) {
      console.error('Failed to download Excel report:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to download Excel report: ${errorMessage}`);
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setIsDownloadingPDF(true);
      await downloadReport('pdf', 'employees');
    } catch (error) {
      console.error('Failed to download PDF report:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to download PDF report: ${errorMessage}`);
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  const {
    totalEmployees,
    activeEmployees,
    totalStoriesCompleted,
    totalXP,
    totalTimeSpent,
    averageXPPerEmployee,
    averageStoriesPerEmployee,
    averageTimePerEmployee,
  } = useMemo(() => {
    const total = employees.length;
    const active = employees.filter((e) => e.isActive).length;
    const stories = employees.reduce((sum, e) => sum + (e.stats?.storiesCompleted ?? 0), 0);
    const xp = employees.reduce((sum, e) => sum + (e.stats?.totalXP ?? 0), 0);
    const time = employees.reduce((sum, e) => sum + (e.stats?.totalTimeSpent ?? 0), 0);
    return {
      totalEmployees: total,
      activeEmployees: active,
      totalStoriesCompleted: stories,
      totalXP: xp,
      totalTimeSpent: time,
      averageXPPerEmployee: total > 0 ? Math.round(xp / total) : 0,
      averageStoriesPerEmployee: total > 0 ? Math.round(stories / total) : 0,
      averageTimePerEmployee: total > 0 ? Math.round(time / total) : 0,
    };
  }, [employees]);

  return (
    <div className={styles.reportsPage}>
      <div className={styles.reportsPage__header}>
        <div>
          <h1 className={styles.reportsPage__title}>Reports</h1>
          <p className={styles.reportsPage__subtitle}>
            Generate and download employee progress reports
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Report Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className={styles.reportsPage__config}>
            <div className={styles.reportsPage__configGroup}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className={styles.reportsPage__label}>Report Period</label>
              <Select
                value={reportPeriod}
                onValueChange={(value) => setReportPeriod(value as ReportPeriod)}
                options={[
                  { label: 'Current Month', value: 'current_month' },
                  { label: 'Last Month', value: 'last_month' },
                  { label: 'Custom Period', value: 'custom' },
                ]}
              />
            </div>

            {reportPeriod === 'custom' && (
              <div className={styles.reportsPage__dateRange}>
                <div className={styles.reportsPage__configGroup}>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className={styles.reportsPage__label}>Start Date</label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className={styles.reportsPage__configGroup}>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className={styles.reportsPage__label}>End Date</label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}

            <div className={styles.reportsPage__actions}>
              <Button
                variant="outline"
                size="md"
                onClick={handleDownloadExcel}
                disabled={isDownloadingExcel || isLoading}
              >
                <FileSpreadsheet size={16} />
                {isDownloadingExcel ? 'Downloading...' : 'Download Excel'}
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleDownloadPDF}
                disabled={isDownloadingPDF || isLoading}
              >
                <FileText size={16} />
                {isDownloadingPDF ? 'Downloading...' : 'Download PDF'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className={styles.reportsPage__summary}>
        <Card>
          <CardContent className={styles.reportsPage__statCard}>
            <Users className={styles.reportsPage__statIcon} />
            <div className={styles.reportsPage__statContent}>
              <p className={styles.reportsPage__statLabel}>Total Employees</p>
              <p className={styles.reportsPage__statValue}>{totalEmployees}</p>
              <Badge variant="default" className={styles.reportsPage__statBadge}>
                {activeEmployees} active
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className={styles.reportsPage__statCard}>
            <TrendingUp className={styles.reportsPage__statIcon} />
            <div className={styles.reportsPage__statContent}>
              <p className={styles.reportsPage__statLabel}>Total XP Earned</p>
              <p className={styles.reportsPage__statValue}>{totalXP.toLocaleString()}</p>
              <Badge variant="success" className={styles.reportsPage__statBadge}>
                Avg: {averageXPPerEmployee} per employee
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className={styles.reportsPage__statCard}>
            <FileText className={styles.reportsPage__statIcon} />
            <div className={styles.reportsPage__statContent}>
              <p className={styles.reportsPage__statLabel}>Stories Completed</p>
              <p className={styles.reportsPage__statValue}>{totalStoriesCompleted}</p>
              <Badge variant="default" className={styles.reportsPage__statBadge}>
                Avg: {averageStoriesPerEmployee} per employee
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className={styles.reportsPage__statCard}>
            <Clock className={styles.reportsPage__statIcon} />
            <div className={styles.reportsPage__statContent}>
              <p className={styles.reportsPage__statLabel}>Total Time Spent</p>
              <p className={styles.reportsPage__statValue}>{formatTimeSpent(totalTimeSpent)}</p>
              <Badge variant="default" className={styles.reportsPage__statBadge}>
                Avg: {formatTimeSpent(averageTimePerEmployee)} per employee
              </Badge>
            </div>
          </CardContent>
        </Card>

        {learningStats !== null && (
          <Card>
            <CardContent className={styles.reportsPage__statCard}>
              <BookOpen className={styles.reportsPage__statIcon} />
              <div className={styles.reportsPage__statContent}>
                <p className={styles.reportsPage__statLabel}>Words Learned This Month</p>
                <p className={styles.reportsPage__statValue}>{learningStats.wordsLearnedThisMonth}</p>
                {learningStats.skillBreakdown.length > 0 && (
                  <Badge variant="success" className={styles.reportsPage__statBadge}>
                    {learningStats.skillBreakdown.length} skills practiced
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default ReportsPage;
